import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, TrendingUp } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import api from "@/lib/api";

const PostCard = ({ p, testid }) => (
  <Link to={`/blog/${p.slug}`} data-testid={testid} className="group relative block aspect-[4/5] overflow-hidden bg-ink">
    <img src={p.coverImage} alt={p.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-6">
      <div className="mb-2 text-[11px] uppercase tracking-[0.15em] text-white/60">{p.readMinutes} min · {p.tags?.[0]}</div>
      <h2 className="font-serif text-xl leading-tight text-white">{p.title}</h2>
      <span className="mt-3 inline-flex items-center gap-1.5 font-sans text-xs text-white/90 transition-transform group-hover:translate-x-1">Read the blog <ArrowRight size={13} /></span>
    </div>
  </Link>
);

export default function BlogIndex() {
  const [posts, setPosts] = useState([]);
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTag, setActiveTag] = useState("All");

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    api.get("/blog-popular").then((r) => setPopular(r.data || [])).catch(() => {});
  }, []);

  const tags = useMemo(() => {
    const map = new Map();
    posts.forEach((p) => (p.tags || []).forEach((t) => {
      if (t) { const k = t.toLowerCase(); if (!map.has(k)) map.set(k, t); }
    }));
    return Array.from(map.values());
  }, [posts]);

  const isAll = activeTag === "All";
  const featured = isAll ? posts[0] : null;
  const grid = isAll ? posts.slice(1) : posts.filter((p) => (p.tags || []).some((t) => t.toLowerCase() === activeTag.toLowerCase()));

  return (
    <>
      <Seo
        title="The Journal — Mental Health & Wellness Notes | Let Go Wellness"
        description="Gentle, practical reading on therapy, anxiety, stress, insurance and getting started — from Let Go Wellness in Waterbury, CT."
        path="/blog"
      />
      <section className="border-b border-border">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-24">
          <Reveal>
            <div className="overline mb-6">The Journal</div>
            <h1 className="max-w-3xl font-serif text-5xl leading-tight text-ink sm:text-6xl">Gentle notes on <span className="italic text-clay">healing.</span></h1>
            <p className="mt-6 max-w-xl font-sans text-base leading-relaxed text-ink-soft">Practical, unhurried reading on therapy, the nervous system, and coming home to yourself.</p>
          </Reveal>
        </div>
      </section>

      {isAll && popular.length > 0 && (
        <section className="border-b border-border bg-white py-14" data-testid="popular-strip">
          <div className="mx-auto max-w-6xl px-6">
            <div className="overline mb-6 flex items-center gap-2"><TrendingUp size={13} className="text-clay" /> Popular this week</div>
            <div className="grid gap-px overflow-hidden border border-border bg-border md:grid-cols-3">
              {popular.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`popular-${p.slug}`} className="group flex items-start gap-4 bg-white p-6 transition-colors hover:bg-linen">
                  <div className="h-16 w-16 shrink-0 overflow-hidden"><img src={p.coverImage} alt={p.title} className="h-full w-full object-cover" loading="lazy" /></div>
                  <div>
                    <div className="overline mb-1">{p.tags?.[0]}</div>
                    <h3 className="font-serif text-lg leading-snug text-ink group-hover:text-clay">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          {tags.length > 0 && (
            <div className="mb-12 flex flex-wrap gap-2" data-testid="blog-filters">
              {["All", ...tags].map((t) => (
                <button
                  key={t}
                  data-testid={`filter-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setActiveTag(t)}
                  className={`rounded-full border px-4 py-2 font-sans text-xs capitalize transition-colors ${activeTag === t ? "border-ink bg-ink text-linen" : "border-border text-ink-soft hover:bg-linen"}`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <p className="font-sans text-sm text-muted-foreground">Loading…</p>
          ) : grid.length === 0 && !featured ? (
            <p className="font-sans text-sm text-muted-foreground" data-testid="blog-empty">No articles in this topic yet — check back soon.</p>
          ) : (
            <>
              {featured && (
                <Link to={`/blog/${featured.slug}`} data-testid="blog-featured" className="group relative mb-8 block aspect-[4/3] overflow-hidden bg-ink sm:aspect-[16/9]">
                  <img src={featured.coverImage} alt={featured.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="eager" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-8 md:p-12">
                    <div className="mb-3 text-[11px] uppercase tracking-[0.15em] text-white/60">Latest · {featured.readMinutes} min · {featured.tags?.[0]}</div>
                    <h2 className="max-w-2xl font-serif text-3xl leading-tight text-white sm:text-4xl">{featured.title}</h2>
                    <p className="mt-3 max-w-xl font-sans text-sm text-white/80">{featured.excerpt}</p>
                    <span className="mt-6 inline-flex items-center gap-2 font-sans text-sm text-white transition-transform group-hover:translate-x-1">Read the blog <ArrowRight size={14} /></span>
                  </div>
                </Link>
              )}
              {grid.length > 0 && (
                <div className="grid gap-px overflow-hidden border border-border bg-border md:grid-cols-3" data-testid="blog-list">
                  {grid.map((p) => (<PostCard key={p.slug} p={p} testid={`blog-card-${p.slug}`} />))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <NewsletterSignup />
    </>
  );
}
